// src/firebase/meetings.service.js
// ────────────────────────────────────────────────────────────
// Service layer for fetching Calendly meetings from Firestore.
// Meetings are synced to Firestore by the backend; the frontend
// reads them directly (consistent with existing service patterns).
// ────────────────────────────────────────────────────────────
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    getDocs,
    Timestamp,
    doc,
    getDoc,
} from 'firebase/firestore';
import { db, auth } from './firebase.config';

/**
 * Fetch upcoming meetings for the authenticated user.
 * Only returns active meetings with start_time >= now, sorted ascending.
 *
 * @param {string} userId - Firebase user UID
 * @param {number} [maxResults=5] - Maximum meetings to return
 * @returns {Promise<Array<{ id, name, inviteeEmail, inviteeName, startTime, endTime, status, location }>>}
 */
export const getUpcomingMeetings = async (userId, maxResults = 5) => {
    if (!userId) throw new Error('userId is required');

    const now = Timestamp.now();
    const meetingsRef = collection(db, 'meetings');

    const q = query(
        meetingsRef,
        where('userId', '==', userId),
        where('status', '==', 'active'),
        where('startTime', '>=', now),
        orderBy('startTime', 'asc'),
        firestoreLimit(maxResults)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            name: data.name || 'Untitled Meeting',
            inviteeEmail: data.inviteeEmail || '',
            inviteeName: data.inviteeName || '',
            startTime: data.startTime?.toDate?.() || new Date(data.startTime),
            endTime: data.endTime?.toDate?.() || new Date(data.endTime),
            status: data.status,
            location: data.location || '',
            timezone: data.timezone || '',
        };
    });
};

/**
 * Check if the current user has Calendly connected.
 * Reads from the public integration status document.
 *
 * @param {string} userId
 * @returns {Promise<{ connected: boolean, email?: string, name?: string, lastSync?: Date }>}
 */
export const getCalendlyStatus = async (userId) => {
    if (!userId) return { connected: false };

    const integrationDoc = await getDoc(
        doc(db, 'users', userId, 'integrations', 'calendly')
    );

    if (!integrationDoc.exists()) {
        return { connected: false };
    }

    const data = integrationDoc.data();
    return {
        connected: !!data.connected,
        email: data.email || null,
        name: data.name || null,
        lastSync: data.lastSync?.toDate?.() || null,
        connectedAt: data.connectedAt?.toDate?.() || null,
    };
};

/**
 * Get the backend URL from environment.
 * Throws if REACT_APP_BACKEND_URL is not set — no silent fallbacks.
 */
export const getBackendUrl = () => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) {
        throw new Error('REACT_APP_BACKEND_URL is not configured');
    }
    return url;
};

/**
 * Initiate Calendly OAuth connect flow via browser redirect.
 *
 * Navigates the browser to the backend /connect endpoint which
 * will redirect to Calendly → back to the backend callback →
 * finally redirect to frontend /settings?calendly=success|error.
 *
 * This is a full-page redirect (no popups).
 */
export const connectCalendly = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('You must be signed in to connect Calendly');
    }

    const idToken = await currentUser.getIdToken();
    const backendUrl = getBackendUrl();
    const connectUrl = `${backendUrl}/api/integrations/calendly/connect?idToken=${encodeURIComponent(idToken)}`;

    console.log('[calendly] Redirecting to backend connect endpoint');
    window.location.href = connectUrl;
};

/**
 * Disconnect Calendly by calling the backend API.
 *
 * @returns {Promise<boolean>}
 */
export const disconnectCalendly = async () => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) return false;

        const idToken = await currentUser.getIdToken();
        const backendUrl = getBackendUrl();

        const res = await fetch(`${backendUrl}/api/integrations/calendly/disconnect`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        return res.ok;
    } catch (err) {
        console.error('[calendly] Disconnect error:', err);
        return false;
    }
};

/**
 * Trigger a manual sync of Calendly events.
 *
 * @returns {Promise<{ success: boolean, synced?: number }>}
 */
export const syncCalendlyEvents = async () => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) return { success: false };

        const idToken = await currentUser.getIdToken();
        const backendUrl = getBackendUrl();

        const res = await fetch(`${backendUrl}/api/integrations/calendly/sync`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!res.ok) return { success: false };
        return await res.json();
    } catch (err) {
        console.error('[calendly] Sync error:', err);
        return { success: false };
    }
};
